import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PanelTitle } from "./ui";
import { SocialsForm } from "./marketing/SocialsForm";
import { MaterialGrid } from "./marketing/MaterialGrid";
import { UploadBox } from "./marketing/UploadBox";
import { ShareSheet } from "./marketing/ShareSheet";
import { WeekPlan } from "./marketing/WeekPlan";
import { ClickStats } from "./marketing/ClickStats";

const Step = ({ n, title, text, children, testId }) => (
  <section className="mt-8" data-testid={testId}>
    <div className="flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink font-display text-sm font-bold text-white">{n}</span>
      <div><h3 className="font-display text-xl font-semibold leading-tight">{title}</h3><p className="text-sm text-ink/70">{text}</p></div>
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

export const MarketingPanel = () => {
  const [d, setD] = useState(null);
  const [sheet, setSheet] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/marketing");
      setD(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin" />;

  const all = [...d.materials, ...d.mine];
  const toggle = async (p) => {
    try {
      await api.post(`/me/marketing/posts/${p.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.title}"?`)) return;
    await api.delete(`/me/marketing/materials/${m.slug}`);
    load();
  };

  return (
    <div data-testid="marketing-panel">
      <PanelTitle title="Marketing en tus redes" text="Paso a paso: conecta tus cuentas, elige un material, compártelo con un toque en WhatsApp, Facebook, Instagram, TikTok o YouTube, sigue tu plan semanal y mira cuántas personas abren tus enlaces. Cada enlace lleva tu ID: quien se registre queda en tu red." />
      <Step n={1} title="Conecta tus redes" text="Tus cuentas personales. Solo tú las ves." testId="step-socials">
        <SocialsForm socials={d.socials} onSaved={(s) => setD({ ...d, socials: s })} />
      </Step>
      <Step n={2} title="Elige el material" text="Lo que publica el administrador llega a toda la red. También puedes subir tus propios PDF, videos o imágenes para tus contactos." testId="step-materials">
        <MaterialGrid items={d.materials} clicks={d.stats.by_slug} onPick={(m) => setSheet({ material: m })} />
        <h4 className="mt-8 font-display text-lg font-semibold">Mis archivos</h4>
        <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1fr),minmax(0,2fr)]">
          <UploadBox onUploaded={load} />
          {d.mine.length ? <MaterialGrid items={d.mine} clicks={d.stats.by_slug} onPick={(m) => setSheet({ material: m })} onDelete={remove} testPrefix="mine" /> : <p className="self-center text-sm text-ink/60" data-testid="mine-empty">Aún no has subido archivos propios.</p>}
        </div>
      </Step>
      <Step n={3} title="Comparte con un toque" text="Toca 'Compartir' en cualquier material: se abre la red elegida con el texto listo y tu enlace personal." testId="step-share">
        <p className="rounded-xl bg-leaf/15 px-4 py-3 text-sm text-leaf-deep">Consejo: en el celular el archivo se adjunta automáticamente; en computadora se descarga y el texto queda copiado.</p>
      </Step>
      <Step n={4} title="Tu plan de la semana" text="Publica una vez al día y mantén tu racha." testId="step-week">
        <WeekPlan week={d.week} streak={d.streak} materials={all} onShare={(m, p) => setSheet({ material: m, post: p })} onToggle={toggle} />
      </Step>
      <Step n={5} title="Resultados" text="Cuántas personas abren tus enlaces y desde qué red." testId="step-stats">
        <ClickStats stats={d.stats} materials={all} />
      </Step>
      {sheet && <ShareSheet material={sheet.material} post={sheet.post} distId={d.dist_id} shareBase={d.share_base} onClose={() => setSheet(null)} onShared={() => { setSheet(null); load(); }} />}
    </div>
  );
};
